import _ from 'lodash';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Flex, WhiteSection } from 'components/base/layout-components';
import ProjectTopBar from './ProjectTopBar';

import ProjectExtendedContent from './ProjectExtendedContent';
import {
  CUSTOM_DEFAULT_END_DATE,
  CUSTOM_DEFAULT_START_DATE,
  SINGLE_ITERATIONS_PERIODS,
  SUMMARY_PERIODS,
  SUMMARY_PERIODS_DEFAULT
} from '../projects.consts';

import { useHistory, useParams } from 'react-router';

import ProjectsEmptyState from 'images/projects-empty-state.svg';
import QueryString from 'query-string';
import { PROJECT_STATUS } from '@linearb/linjs-common';
import {
  getPdtProject,
  getProjectContributors,
  getProjectIterations
} from 'common/services/pdt.service';
import AppRouter from 'common/AppRouter';
import { checkStatusChanges } from '../services/projects.services';
import getProjectsFavoritesService from '../services/projectsFavorites.services';
import useIsPulseKanbanAccumulatedDoneDisabled from './useIsPulseKanbanAccumulatedDoneDisabled';

const EmptyStateImage = styled.img`
  width: 100%;
`;

const StyledBackground = styled.div`
  width: 100%;

  .highcharts-container {
    pointer-events: none;
  }
`;
const getQueryParams = ({
  selectedPeriod,
  selectedIteration,
  selectedTimeRange,
  isAccumlatedDone
}) => {
  const params = [];
  if (selectedPeriod) {
    params.push(`duration_filter=${selectedPeriod}`);
  }
  if (selectedIteration) {
    if (selectedIteration.id) {
      params.push(`iteration_id=${selectedIteration.id}`);
    } else if (selectedIteration.provider_id) {
      params.push(`iteration_provider_id=${selectedIteration.provider_id}`);
    }
  }
  if (selectedTimeRange) {
    const startDate = _.get(selectedTimeRange, 'start_date');
    const endDate = _.get(selectedTimeRange, 'end_date');
    params.push(`start_date=${startDate}`);
    params.push(`end_date=${endDate}`);
  }

  if (isAccumlatedDone !== undefined) {
    params.push(`is_accumulated_done=${isAccumlatedDone}`);
  }

  return params.join('&');
};
const ProjectExtended = ({
  pmSensorAuthId,
  isCanConfigProjects,
  toggleModal,
  toggleSnackBar,
  pdtProjectId = null,
  duration = null,
  currentSelectedIteration = null,
  showTopBar = true,
  account,
  EmptyStateComponent = null,
  isRetrospectView,
  iterationSummary,
  onClickTrendIteration = null,
  isLoading = false
}) => {
  const history = useHistory();
  const { uuid: paramsProjectUuid } = useParams();
  const projectUuid = paramsProjectUuid || pdtProjectId;
  const search = QueryString.parse(window.location.search, {
    ignoreQueryPrefix: true
  });

  const [selectedPeriod, setSelectedPeriod] = useState(
    _.get(search, 'duration_filter', duration || SUMMARY_PERIODS_DEFAULT)
  );
  const [selectedTimeRange, setSelectedTimeRange] = useState({
    start_date: _.get(search, 'start_date', CUSTOM_DEFAULT_START_DATE),
    end_date: _.get(search, 'end_date', CUSTOM_DEFAULT_END_DATE)
  });
  const [lastIteration, setLastIteration] = useState({});
  const [selectedIteration, setSelectedIteration] = useState(
    currentSelectedIteration
  );
  const [lastSelectedPeriod, setLastSelectedPeriod] = useState();
  const [allIterationsContributors, setAllIterationsContributors] = useState(
    []
  );
  const [contributorsLoading, setContributorsLoading] = useState(true);
  const [contributors, setContributors] = useState(null);
  const [allIterations, setAllIterations] = useState();
  const [iterationsLoading, setIterationsLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [projectLoading, setProjectLoading] = useState(false);

  const isPulseKanbanAccumulatedDoneDisabled =
    useIsPulseKanbanAccumulatedDoneDisabled();

  const { isFavoriteProject } = getProjectsFavoritesService({
    account
  });

  useEffect(() => {
    (async () => {
      setProjectLoading(true);
      const result = await getPdtProject({
        projectId: projectUuid,
        queryParams: getQueryParams({
          selectedPeriod,
          selectedIteration,
          selectedTimeRange,
          ...(isRetrospectView &&
            isPulseKanbanAccumulatedDoneDisabled && { isAccumlatedDone: false })
        })
      });
      if ((!result || !result.id) && !EmptyStateComponent) {
        history.push({
          pathname: AppRouter.PROJECTS_API.path
        });
      }
      setProject(result);
      setProjectLoading(false);
    })();
  }, [selectedPeriod, selectedIteration, selectedTimeRange]);

  const onProjectEdited = ({ upsertedProject }) => {
    toggleModal();
    setProject({
      ...project,
      ...upsertedProject,
      status: PROJECT_STATUS.EDITED
    });
  };

  useEffect(() => {
    const interval =
      project &&
      checkStatusChanges({
        projects: [project],
        onChange: mapResult =>
          setProject(mapResult[_.get(project, 'id')] || project),
        selectedPeriod
      });
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [project]);

  useEffect(() => {
    (async () => {
      try {
        setContributorsLoading(true);
        const result = await getProjectContributors({
          projectUuid,
          queryParams: getQueryParams({
            selectedPeriod,
            selectedIteration,
            selectedTimeRange
          })
        });
        setContributors(result);
        setContributorsLoading(false);
      } catch (exp) {
        setContributorsLoading(false);
      }
    })();
  }, [selectedPeriod, selectedIteration, selectedTimeRange]);

  useEffect(() => {
    (async () => {
      try {
        setIterationsLoading(true);

        const result = await getProjectIterations(projectUuid);
        setAllIterations(result);
        setIterationsLoading(false);
      } catch (exp) {
        setIterationsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (duration) setSelectedPeriod(duration);
  }, [duration]);

  const isScrumProject =
    _.get(project, 'cadence.type', '').toLowerCase() === 'scrum';

  const isSingleIterationView =
    SINGLE_ITERATIONS_PERIODS.includes(selectedPeriod);

  useEffect(() => {
    const lastIteration = allIterations?.at(-1);
    setLastIteration(lastIteration);
    if (selectedPeriod === SUMMARY_PERIODS.last_sprint) {
      setSelectedIteration(lastIteration);
    }
    if (allIterations) {
      const iterationContributorsMap = allIterations.reduce(function (
        map,
        obj
      ) {
        map[obj.id] = obj.contributors;
        return map;
      },
      {});

      setAllIterationsContributors(iterationContributorsMap);
    }
  }, [allIterations]);
  const loading =
    isLoading ||
    iterationsLoading ||
    (projectLoading && !project) ||
    (contributorsLoading && !contributors);
  if (loading) {
    return (
      <StyledBackground>
        <WhiteSection>
          <EmptyStateImage
            src={ProjectsEmptyState}
            alt='Projects empty state'
          />
        </WhiteSection>
      </StyledBackground>
    );
  }

  return (
    <StyledBackground>
      {showTopBar && (
        <WhiteSection>
          <Flex
            pl='medium'
            items='center'
            height='5.7rem'
            margin='0 0 0.8rem 0'
          >
            <ProjectTopBar
              project={project}
              lastUpdated={
                _.get(project, 'updated_at') || _.get(project, 'created_at')
              }
              lastIteration={lastIteration}
              pmSensorAuthId={pmSensorAuthId}
              toggleModal={toggleModal}
              toggleSnackBar={toggleSnackBar}
              isCanConfigProjects={isCanConfigProjects}
              selectedIteration={selectedIteration}
              setSelectedIteration={setSelectedIteration}
              selectedPeriod={selectedPeriod}
              setSelectedPeriod={setSelectedPeriod}
              lastSelectedPeriod={lastSelectedPeriod}
              setLastSelectedPeriod={setLastSelectedPeriod}
              onProjectEdited={onProjectEdited}
              history={history}
              isSingleIterationView={isSingleIterationView}
              iterations={allIterations || []}
              isFavorite={isFavoriteProject(project)}
              selectedTimeRange={selectedTimeRange}
              setSelectedTimeRange={setSelectedTimeRange}
            />
          </Flex>
        </WhiteSection>
      )}

      {_.get(project, 'iterations', [])?.length ? (
        <WhiteSection style={{ position: 'relative' }}>
          <ProjectExtendedContent
            lastSelectedPeriod={lastSelectedPeriod}
            setLastSelectedPeriod={setLastSelectedPeriod}
            selectedIteration={selectedIteration}
            setSelectedIteration={setSelectedIteration}
            projectLoading={projectLoading}
            allIterationsContributors={allIterationsContributors}
            project={project}
            contributors={contributors}
            contributorsLoading={contributorsLoading}
            isSingleIterationView={isSingleIterationView}
            isScrumProject={isScrumProject}
            selectedPeriod={selectedPeriod}
            setSelectedPeriod={setSelectedPeriod}
            lastIteration={lastIteration}
            allIterations={allIterations}
            isRetrospectView={isRetrospectView}
            iterationSummary={iterationSummary}
            onClickTrendIteration={onClickTrendIteration}
          />
        </WhiteSection>
      ) : EmptyStateComponent ? (
        <EmptyStateComponent
          selectedPeriod={selectedPeriod}
          selectedIteration={selectedIteration}
          projectUuid={projectUuid}
        />
      ) : (
        <></>
      )}
    </StyledBackground>
  );
};

export default ProjectExtended;
